<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-container>
      <template v-if="isUpdate && !overWriteTaskGroup">
        <dl class="dl">
          <dt v-if="commonAssessmentConfig">Assessment Config</dt>
          <dd v-if="commonAssessmentConfig">{{ commonAssessmentConfig.name }}</dd>
          <dt v-if="commonAssessmentConfig">Tasks</dt>
          <dd v-if="commonAssessmentConfig">
            <task-config-summary :taskConfig="commonAssessmentConfig.config.tasks"/>
          </dd>
          <dd v-else-if="commonAssessmentConfig === null"><em>No assessment config assigned</em></dd>
          <dd v-else><em>Multiple task groups selected</em></dd>
        </dl>
      </template>
      <template v-else>
        <tabs :tabs="tabs" :active-tab.sync="tasksTab">
          <template v-if="!custom">
            <v-item-group v-model="assessmentConfigSelected" class="task-group-items">
              <v-container grid-list-md>
                <v-layout justify-start row class="task-row">
                  <v-flex v-for="(assessmentConfig, i) in assessmentConfigsList"
                          :key="`assessmentConfig-${i}`"
                          class="task-col" xs6>
                    <v-item>
                      <v-card @click="toggle" slot-scope="{ active, toggle }"
                              height="105"
                              :color="active ? 'primary lighten-4' : ''"
                              :data-test="`assessmentConfigCard-${i}`"
                              :data-test-task-group-id="i">
                        <v-toolbar :color="active ? 'primary' : 'grey lighten-4'" flat dense class="pr-0" :dark="active">
                          <v-toolbar-title>{{ assessmentConfig.name }}</v-toolbar-title>
                          <v-spacer/>
                        </v-toolbar>
                        <v-card-actions>
                          <task-config-summary :taskConfig="assessmentConfig.config.tasks"/>
                        </v-card-actions>
                      </v-card>
                    </v-item>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-item-group>
          </template>
          <template v-else>
             <task-config :config="customConfig.tasks" title="Custom Tasks" class="pl-3 pb-1"/>
          </template>
        </tabs>
      </template>
      <v-layout>
        <v-flex pt-3  >
          <v-toolbar color="white" flat>
            <v-radio-group v-if="!custom" v-model="showAllConfigs" row class="text-xs-right">
                <template v-slot:label>
                <div>Show Configs</div>
              </template>
              <v-radio
                label="Promoted"
                :value="false"
              ></v-radio>
              <v-radio
                label="All"
                :value="true"
              ></v-radio>
            </v-radio-group>
            <v-spacer></v-spacer>
            <template v-if="isUpdate && !overWriteTaskGroup">
              <v-btn color="primary darken-1" @click="overWriteTaskGroup = true" class="pl-3">
                <v-icon class="px-1" left>edit</v-icon>
                Change Assigned Tasks
              </v-btn>
            </template>
            <v-btn v-if="isUpdate && this.data.updateType === 'override' && canUndo"
                   color="secondary darken-2"
                   @click="undoAll">
              <v-icon class="px-1" left>refresh</v-icon>
              Reset
            </v-btn>
            <v-btn :disabled="!canAdvance"
                   color="success darken-2"
                   @click="goToNext"
                   class="pl-4 ml-3"
                   data-test="finishTasksAssignment">
              <v-icon class="px-1" left>check</v-icon>
              Next
            </v-btn>
          </v-toolbar>
        </v-flex>
      </v-layout>
    </v-container>
    <confirm-dialog ref="confirm" @confirm="next" data-test="confirmSurveyAsFinalTask">
      <p class="mb-0">You do not have <strong>a survey as the final task</strong>.</p>
      <p class="mt-3 mb-0">Are you sure this is correct?</p>
    </confirm-dialog>
  </v-form>
</template>

<script>
import { mapState } from 'vuex'
import FormStep from '@/mixins/FormStep'
import CurrentSession from '@/mixins/CurrentSession'
import ConfirmDialog from '@/components/ConfirmDialog'
import Account from '@/mixins/Account'
import TaskConfigSummary from '@/components/Tasks/TaskConfigSummary'
import Tabs from '@/components/Tabs'
import TaskConfig from '@/components/Tasks/TaskConfig'
import { Bus } from '@/bus'
import { config } from '@/helpers'
import UndoRedoMixin from '@/mixins/UndoRedo'

export default {
  name: 'AccountAssessmentConfig',
  components: {
    TaskConfig,
    TaskConfigSummary,
    Tabs,
    ConfirmDialog
  },
  mixins: [ Account, FormStep, CurrentSession, UndoRedoMixin ],
  data () {
    return {
      assessmentConfigSelected: null,
      overWriteTaskGroup: false,
      showAllConfigs: false,
      config: config,
      customConfig: {
        tasks: { names: [], parameters: {} }
      },
      tabs: ['Standard Assessment Config', 'Custom Assessment Config'],
      tasksTab: 0
    }
  },
  computed: {
    ...mapState('assessmentConfigs', {
      assessmentConfigs: 'assessmentConfigs'
    }),
    assessmentConfigsList () {
      return Object.values(this.assessmentConfigs).filter((config) => this.showAllConfigs || config.position > 0)
    },
    commonAssessmentConfig () {
      if (this.data.commonAssessmentConfigId > 0) {
        return this.assessmentConfigs[this.data.commonAssessmentConfigId]
      }

      if (this.data.user && this.data.user.assessment_config && this.data.user.assessment_config.tasks) {
        return { name: 'Custom', config: this.data.user.assessment_config }
      }

      return null
    },
    custom () {
      return this.tasksTab === 1
    },
    canAdvance () {
      return (
        (this.isUpdate && !this.overWriteTaskGroup) ||
        (this.assessmentConfigSelected === null && this.customConfig.tasks.names.length > 0) ||
        this.assessmentConfigSelected != null)
    }
  },
  methods: {
    entered () {
      if (this.data.updateType !== 'override') {
        this.overWriteTaskGroup = true
      }

      if (this.data.user && this.data.user.assessment_config && this.data.user.assessment_config.tasks) {
        this.customConfig = this.data.user.assessment_config
        this.tasksTab = 1
      }
    },
    formData () {
      return {
        assessmentConfigId: this.assessmentConfigSelected !== null ? this.assessmentConfigsList[this.assessmentConfigSelected].id : null,
        customConfig: this.assessmentConfigSelected !== null ? null : this.customConfig
      }
    },
    goToNext () {
      if (this.isCandidate() && !this.isValidConfigWithSurvey()) {
        Bus.$emit('alert', {
          color: 'error',
          message: 'You cannot create candidate accounts without a survey as final task' })
      } else if (!this.isValidConfigWithSurvey()) {
        this.$refs.confirm.shown = true
      } else {
        this.next()
      }
    },
    isCandidate () {
      return this.data.users.some((user) => user.user_type === 'Candidate') || this.data.user.user_type === 'Candidate'
    },
    isValidConfigWithSurvey () {
      if (this.isUpdate && !this.overWriteTaskGroup) {
        return true
      }

      let taskConfig = this.getTaskConfig()
      let lastTaskName = taskConfig.names[taskConfig.names.length - 1]

      return 'survey_url' in (taskConfig.parameters[lastTaskName] || {})
    },
    getTaskConfig () {
      if (this.assessmentConfigSelected === null) {
        return this.customConfig.tasks
      }
      return this.assessmentConfigsList[this.assessmentConfigSelected].config.tasks
    }
  },
  watch: {
    /**
     * Clean content when switching tabs
     *
     * @param activeTab
     */
    tasksTab (activeTab) {
      if (activeTab === 1) {
        this.assessmentConfigSelected = null
      }
    },
    overWriteTaskGroup (value) {
      /**
       * Preselect Default task group if exists and shared between accounts
       */
      if (this.data.updateType === 'override' && value && this.data.commonAssessmentConfigId) {
        this.assessmentConfigSelected = this.assessmentConfigsList.findIndex(assessmentConfig => assessmentConfig.id === this.data.commonAssessmentConfigId) || null
      }
    }
  }
}
</script>

<style lang="sass">
.task-row
  flex-wrap: wrap !important

  .task-col
    max-width: 33% !important

.task-config
  margin-top: -16px

  ul
    list-style-type: none

.cards-enter-active
  transition: all 0.2s

.cards-enter
  opacity: 0
  transform: translateY(30px)

.taskTitle
  margin: 0

.dl
  display: grid
  grid-template-columns: max-content auto
</style>
